import { useEffect, useState, useCallback } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, RefreshCw, Check, X, Loader2,
  Zap, DollarSign, ArrowRight, CheckCheck, XCircle
} from "lucide-react";

export default function RepricingPage() {
  const { t, language } = useI18n();
  const [proposals, setProposals] = useState([]);
  const [stats, setStats] = useState(null);
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [tab, setTab] = useState("pending");

  const fetchData = useCallback(() => {
    Promise.all([
      api.get("/repricing/proposals"),
      api.get("/repricing/stats"),
      api.get("/repricing/settings"),
    ]).then(([p, s, settings]) => {
      setProposals(p.data);
      setStats(s.data);
      setAutoEnabled(settings.data.auto_repricing);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await api.post("/repricing/generate");
      toast.success(`${res.data.proposals_generated} ${t("toast.proposals_generated")}`);
      if (res.data.products_without_cost > 0) {
        const n = res.data.products_without_cost;
        toast.info(language === "nl" ? `${n} producten overgeslagen: vul eerst de inkoopprijs in.`
          : language === "fr" ? `${n} produits ignorés : saisissez d'abord le prix d'achat.`
          : `${n} products skipped: enter the purchase price first.`);
      }
      fetchData();
    } catch { toast.error(t("common.error")); }
    finally { setGenerating(false); }
  };

  const handleAccept = async (id) => {
    try {
      await api.post(`/repricing/proposals/${id}/accept`);
      toast.success(t("toast.price_adjusted"));
      fetchData();
    } catch { toast.error(t("common.error")); }
  };

  const handleReject = async (id) => {
    try {
      await api.post(`/repricing/proposals/${id}/reject`);
      toast.success(t("toast.proposal_rejected"));
      fetchData();
    } catch { toast.error(t("common.error")); }
  };

  const handleAcceptAll = async () => {
    try {
      const res = await api.post("/repricing/proposals/accept-all");
      toast.success(`${res.data.accepted} ${t("toast.proposals_accepted")}`);
      fetchData();
    } catch { toast.error(t("common.error")); }
  };

  const handleRejectAll = async () => {
    try {
      const res = await api.post("/repricing/proposals/reject-all");
      toast.success(`${res.data.rejected} ${t("toast.proposals_rejected")}`);
      fetchData();
    } catch { toast.error(t("common.error")); }
  };

  const toggleAuto = async (enabled) => {
    try {
      await api.put("/repricing/settings", { auto_repricing: enabled });
      setAutoEnabled(enabled);
      toast.success(enabled ? t("toast.auto_on") : t("toast.auto_off"));
    } catch { toast.error(t("common.error")); }
  };

  const filtered = proposals.filter((p) => tab === "all" || p.status === tab);

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  const pending = proposals.filter((p) => p.status === "pending");
  const stratLabels = {
    undercut_cheapest: t("repricing.strategy_undercut"),
    match_cheapest: t("repricing.strategy_match"),
    protect_margin: t("repricing.strategy_protect"),
    increase_margin: t("repricing.strategy_increase"),
  };

  return (
    <div className="space-y-6" data-testid="repricing-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">{t("repricing.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("repricing.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-card border border-border rounded-lg px-4 py-2" data-testid="auto-repricing-toggle">
            <Label htmlFor="auto-repricing" className="text-sm font-medium cursor-pointer">{t("repricing.auto_label")}</Label>
            <Switch id="auto-repricing" checked={autoEnabled} onCheckedChange={toggleAuto} data-testid="auto-repricing-switch" />
          </div>
          <Button onClick={handleGenerate} disabled={generating} className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="generate-proposals-btn">
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {t("repricing.generate")}
          </Button>
        </div>
      </div>

      <Card className="bg-card border-border border-l-4 border-l-blue-500" data-testid="repricing-info-card">
        <CardContent className="p-6">
          <h3 className="font-bold text-base mb-3">{t("repricing.how_title")}</h3>
          <div className="text-sm text-muted-foreground leading-relaxed space-y-3">
            <p>{t("repricing.how_desc")}</p>
            <div>
              <p className="font-semibold text-foreground mb-1">{t("repricing.strategies_title")}</p>
              <ul className="list-disc pl-5 space-y-1">
                <li><strong>{t("repricing.strategy_undercut")}</strong> - {t("repricing.strategy_undercut_desc")}</li>
                <li><strong>{t("repricing.strategy_match")}</strong> - {t("repricing.strategy_match_desc")}</li>
                <li><strong>{t("repricing.strategy_protect")}</strong> - {t("repricing.strategy_protect_desc")}</li>
                <li><strong>{t("repricing.strategy_increase")}</strong> - {t("repricing.strategy_increase_desc")}</li>
              </ul>
            </div>
            <p>{t("repricing.how_actions")}</p>
          </div>
        </CardContent>
      </Card>

      {autoEnabled && (
        <Card className="bg-amber-500/10 border-amber-500/30" data-testid="auto-repricing-notice">
          <CardContent className="p-4 flex items-center gap-3">
            <Zap className="w-5 h-5 text-amber-500 shrink-0" />
            <p className="text-sm">{t("repricing.auto_enabled")}</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
        {[
          { label: t("repricing.stat_total"), value: stats?.total || 0, icon: DollarSign, color: "text-blue-500" },
          { label: t("repricing.stat_pending"), value: stats?.pending || 0, icon: RefreshCw, color: "text-amber-500" },
          { label: t("repricing.stat_accepted"), value: stats?.accepted || 0, icon: Check, color: "text-emerald-500" },
          { label: t("repricing.stat_rejected"), value: stats?.rejected || 0, icon: X, color: "text-red-500" },
          { label: t("repricing.stat_impact"), value: `${stats?.total_profit_impact || 0} EUR`, icon: TrendingUp, color: "text-emerald-500", mono: true },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border" data-testid={`repricing-stat-${i}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-muted-foreground">{s.label}</span>
                <s.icon className={`w-3.5 h-3.5 ${s.color}`} />
              </div>
              <p className={`text-xl font-bold ${s.mono ? "font-mono-data" : ""}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {pending.length > 0 && (
        <div className="flex items-center gap-3">
          <Badge variant="secondary">{pending.length} {t("repricing.pending_proposals")}</Badge>
          <Button size="sm" variant="outline" onClick={handleAcceptAll} data-testid="accept-all-btn" className="text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/10">
            <CheckCheck className="w-3.5 h-3.5 mr-1.5" /> {t("repricing.accept_all")}
          </Button>
          <Button size="sm" variant="outline" onClick={handleRejectAll} data-testid="reject-all-btn" className="text-red-500 border-red-500/30 hover:bg-red-500/10">
            <XCircle className="w-3.5 h-3.5 mr-1.5" /> {t("repricing.reject_all")}
          </Button>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending" data-testid="tab-pending">{t("repricing.tab_pending")} ({proposals.filter(p => p.status === "pending").length})</TabsTrigger>
          <TabsTrigger value="accepted" data-testid="tab-accepted">{t("repricing.tab_accepted")}</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">{t("repricing.tab_rejected")}</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">{t("repricing.tab_all")}</TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          <Card className="bg-card border-border overflow-hidden">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("repricing.col_product")}</TableHead>
                    <TableHead className="text-right">{t("repricing.col_current")}</TableHead>
                    <TableHead className="text-center"></TableHead>
                    <TableHead className="text-right">{t("repricing.col_proposal")}</TableHead>
                    <TableHead>{t("repricing.col_competitor")}</TableHead>
                    <TableHead className="text-right">{t("repricing.col_margin")}</TableHead>
                    <TableHead>{t("repricing.col_strategy")}</TableHead>
                    <TableHead className="text-right">{t("repricing.col_actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                      <div className="space-y-2">
                        <p className="font-medium text-foreground">{t("repricing.no_proposals")}</p>
                        <p className="text-sm">{t("repricing.no_proposals_desc")}</p>
                      </div>
                    </TableCell></TableRow>
                  ) : (
                    filtered.map((p) => {
                      const priceDown = p.suggested_price < p.current_price;
                      return (
                        <TableRow key={p.id} className="table-row-hover" data-testid={`proposal-${p.id}`}>
                          <TableCell>
                            <div className="font-medium text-sm">{p.product_name}</div>
                            <div className="text-xs text-muted-foreground font-mono-data">{p.product_sku}</div>
                          </TableCell>
                          <TableCell className="text-right font-mono-data">{p.current_price?.toFixed(2)}</TableCell>
                          <TableCell className="text-center">
                            <ArrowRight className={`w-4 h-4 mx-auto ${priceDown ? "text-red-400" : "text-emerald-400"}`} />
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`font-mono-data font-bold ${priceDown ? "text-red-400" : "text-emerald-400"}`}>
                              {p.suggested_price?.toFixed(2)}
                            </span>
                            <div className="text-xs text-muted-foreground">
                              {p.profit_change > 0 ? "+" : ""}{p.profit_change?.toFixed(2)} EUR/st
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="text-sm">{p.cheapest_competitor}</div>
                            <div className="text-xs text-muted-foreground font-mono-data">{p.cheapest_price?.toFixed(2)} EUR</div>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="text-sm">{p.old_margin_pct}%</div>
                            <div className={`text-xs font-bold ${p.new_margin_pct > p.old_margin_pct ? "text-emerald-400" : "text-amber-400"}`}>
                              {p.new_margin_pct}%
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{stratLabels[p.strategy] || p.strategy}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {p.status === "pending" ? (
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10" onClick={() => handleAccept(p.id)} data-testid={`accept-${p.id}`}>
                                  <Check className="w-4 h-4" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={() => handleReject(p.id)} data-testid={`reject-${p.id}`}>
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ) : (
                              <Badge variant={p.status === "accepted" ? "default" : "secondary"}
                                     className={p.status === "accepted" ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>
                                {p.status === "accepted" ? t("repricing.stat_accepted") : t("repricing.stat_rejected")}
                              </Badge>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {filtered.length > 0 && filtered[0].status === "pending" && (
        <Card className="bg-card border-border" data-testid="proposal-reason-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">{t("repricing.last_proposal")}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{filtered[0].reason}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
